import { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, UserCog, Copy, CheckCircle2, Clock, AlertTriangle, HelpCircle, Ban, ShieldOff, PlayCircle, Mail, RefreshCw, PenLine, Eye, XCircle, LayoutGrid, List, Search, User, Shield, Camera, Link2, PowerOff, RotateCcw, X } from 'lucide-react';

import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { ALL_PERMISSIONS, getPermissionsByRole, type PermissionKey, type PermissionMap, hasPermission, isEscolaDiretorCargo } from '../../auth/permissions';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AvatarCropper } from '../../components/ui/AvatarCropper';
import { Input } from '../../components/ui/input';
import { Dropdown } from '../../components/ui/Dropdown';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { SetupNextStepCard } from '../../components/onboarding/SetupNextStepCard';
import { useDevStore } from '../../store/useDevStore';
import type { Usuario } from '../../types/models';
import { ListAvatar } from '../../components/admin/list-avatar';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { EquipeFichaDesktop, EquipeFichaFooter } from './EquipeFichaDesktop';
import { InviteMemberModal } from './equipe/InviteMemberModal';
import { InviteResultDialog, normalizeActivationLink } from '../../components/invite';
import { EquipeCardView } from './equipe/EquipeCardView';
import { RowActionMenu, type RowActionItem } from '../../components/ui';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { UPLOAD } from '../../api/routes';

const ROLES = ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'];
const CARGOS = ['Diretor(a)', 'Vice Diretor', 'Coordenador(a)', 'Supervisor(a)', 'Orientador(a)', 'Secretário(a)'];

export function formatCargo(cargo?: string | null, perfil?: string | null): string {
  const val = (cargo || perfil || '').trim();
  if (!val) return '—';
  const upper = val.toUpperCase();
  if (upper === 'DIRETOR' || upper === 'DIRETOR(A)') return 'Diretor(a)';
  if (upper === 'VICE_DIRETOR' || upper === 'VICE-DIRETOR' || upper === 'VICE DIRETOR' || upper === 'VICE-DIRETOR(A)' || upper === 'VICE DIRETOR(A)') return 'Vice Diretor';
  if (upper === 'COORDENACAO' || upper === 'COORDENADOR' || upper === 'COORDENADOR(A)' || upper === 'COORDENAÇÃO') return 'Coordenador(a)';
  if (upper === 'SUPERVISOR' || upper === 'SUPERVISOR(A)') return 'Supervisor(a)';
  if (upper === 'ORIENTADOR' || upper === 'ORIENTADOR(A)') return 'Orientador(a)';
  if (upper === 'SECRETARIA' || upper === 'SECRETÁRIA' || upper === 'SECRETARIO' || upper === 'SECRETÁRIO' || upper === 'SECRETÁRIO(A)' || upper === 'SECRETARIO(A)') return 'Secretário(a)';
  return val;
}

const PERFIL_OPTIONS = ROLES.map(r => ({ value: r, label: r === 'VICE_DIRETOR' ? 'Vice Diretor' : r }));
const CARGO_OPTIONS = CARGOS.map(c => ({ value: c, label: c }));

// Mapeamento bidirecional Perfil <-> Cargo
const PERFIL_TO_CARGO: Record<string, string> = {
  DIRETOR: 'Diretor(a)',
  VICE_DIRETOR: 'Vice Diretor',
  'VICE-DIRETOR': 'Vice Diretor',
  COORDENACAO: 'Coordenador(a)',
  SUPERVISOR: 'Supervisor(a)',
  ORIENTADOR: 'Orientador(a)',
  SECRETARIA: 'Secretário(a)',
};
const CARGO_TO_PERFIL: Record<string, string> = {
  'Diretor(a)': 'DIRETOR',
  'Vice Diretor': 'VICE_DIRETOR',
  'Vice-Diretor': 'VICE_DIRETOR',
  'Vice-Diretor(a)': 'VICE_DIRETOR',
  'Vice Diretor(a)': 'VICE_DIRETOR',
  'Coordenador(a)': 'COORDENACAO',
  'Supervisor(a)': 'COORDENACAO',
  'Orientador(a)': 'COORDENACAO',
  'Secretário(a)': 'SECRETARIA',
};

const PERMISSION_GROUPS: { title: string; items: { key: PermissionKey; label: string }[] }[] = [
  {
    title: 'Equipe e Configuração',
    items: [
      { key: 'equipe.view', label: 'Visualizar equipe' },
      { key: 'equipe.manage', label: 'Gerenciar equipe' },
      { key: 'configuracoes.manage', label: 'Gerenciar configurações' },
    ],
  },
  {
    title: 'Acadêmico',
    items: [
      { key: 'turnos.view', label: 'Visualizar turnos' },
      { key: 'turnos.manage', label: 'Gerenciar turnos' },
      { key: 'materias.view', label: 'Visualizar matérias' },
      { key: 'materias.manage', label: 'Gerenciar matérias' },
      { key: 'turmas.view', label: 'Visualizar turmas' },
      { key: 'turmas.manage', label: 'Gerenciar turmas' },
    ],
  },
  {
    title: 'Pessoas e Contas',
    items: [
      { key: 'professores.view', label: 'Visualizar professores' },
      { key: 'professores.manage', label: 'Gerenciar professores' },
      { key: 'alunos.view', label: 'Visualizar alunos' },
      { key: 'alunos.manage', label: 'Gerenciar alunos' },
      { key: 'responsaveis.view', label: 'Visualizar responsáveis' },
      { key: 'responsaveis.manage', label: 'Gerenciar responsáveis' },
    ],
  },
  {
    title: 'Operacional',
    items: [
      { key: 'chamadas.view', label: 'Visualizar chamadas' },
      { key: 'chamadas.manage', label: 'Gerenciar / Gov. de chamadas' },
      { key: 'comunicados.view', label: 'Visualizar comunicados' },
      { key: 'comunicados.manage', label: 'Gerenciar comunicados' },
      { key: 'midias.view', label: 'Visualizar mídias' },
      { key: 'midias.manage', label: 'Gerenciar mídias' },
      { key: 'acesso.view', label: 'Visualizar acesso/portaria' },
      { key: 'acesso.manage', label: 'Gerenciar acesso/portaria' },
    ],
  },
  {
    title: 'Gestão e Infraestrutura',
    items: [
      { key: 'dashboard.view', label: 'Visualizar dashboard' },
      { key: 'relatorios.view', label: 'Visualizar relatórios' },
      { key: 'dispositivos.view', label: 'Visualizar dispositivos' },
      { key: 'dispositivos.manage', label: 'Gerenciar dispositivos' },
      { key: 'gestao.view', label: 'Visualizar dados de gestão (admin)' },
      { key: 'gestao.manage', label: 'Gerenciar dados de gestão (admin)' },
    ],
  },
];

const buildPermissionMap = (perfil: string, cargo: string, source?: any): PermissionMap => {
  const base = getPermissionsByRole('ESCOLA', perfil);
  const custom = source?.preferencias?.permissoes || source?.permissoes;
  const merged = { ...base };
  if (custom && typeof custom === 'object') {
    for (const permission of ALL_PERMISSIONS) {
      if (typeof custom[permission] === 'boolean') merged[permission] = custom[permission];
    }
  }
  if (cargo) {
    const byCargo = getPermissionsByRole('ESCOLA', perfil);
    for (const permission of ALL_PERMISSIONS) {
      if (merged[permission] === undefined) merged[permission] = byCargo[permission];
    }
  }
  return merged;
};

/** Resolve o papel real do membro (cargo do banco pode ser 'DIRETOR', 'COORDENACAO', etc.) */
const resolveRole = (u: any): string => {
  // Tentar perfil primeiro, depois cargo do banco
  if (PERFIL_TO_CARGO[u.perfil]) return u.perfil;
  if (PERFIL_TO_CARGO[u.cargo]) return u.cargo;
  // Cargo pode estar como string bruta ('DIRETOR') no banco
  if (typeof u.cargo === 'string' && ['DIRETOR', 'VICE_DIRETOR', 'COORDENACAO', 'SECRETARIA'].includes(u.cargo)) return u.cargo;
  return u.perfil || 'ESCOLA';
};

/** Máscara telefone BR: (99) 99999-9999 ou (99) 9999-9999 */
const formatPhone = (v: string): string => {
  const digits = v.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export function EquipePage() {
  const { canManage: canManageFn } = usePermissions();
  const canManage = canManageFn('equipe');
  const devSettings = useDevStore((s) => s.settings);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: equipe, isLoading, error: loadError, create, update, deactivate, refetch } = useCrud<Usuario>('equipe');
  const escolaId = useAuthStore((s) => s.user?.escolaId);
  const userId = useAuthStore((s) => s.user?.id);
  const userCargo = useAuthStore((s) => s.user?.cargo);
  const userPerfil = useAuthStore((s) => s.user?.perfil);
  const user = useAuthStore(s => s.user);
  const canManagePermissions = isEscolaDiretorCargo(userCargo) || userPerfil === 'ADMIN';
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [fichaTab, setFichaTab] = useState<'dados' | 'convite'>('dados');
  const [form, setForm] = useState<{
    nome: string;
    email: string;
    perfil: string;
    cargo: string;
    telefone: string;
    permissaoPreset: 'PADRAO' | 'PERSONALIZADO';
    permissoes: PermissionMap;
    /** URL ou data URL da foto (espelha servidor após upload) */
    fotoUrl: string | null;
  }>({
    nome: '',
    email: '',
    perfil: 'SECRETARIA',
    cargo: 'Secretário(a)',
    telefone: '',
    permissaoPreset: 'PADRAO',
    permissoes: getPermissionsByRole('ESCOLA', 'SECRETARIA'),
    fotoUrl: null,
  });
  const [saving, setSaving] = useState(false);
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(12);
  const [deleteConfirm, setDeleteConfirm] = useState<Usuario | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<{ membro: Usuario; novoStatus: string } | null>(null);
  /** Aba do modal convite/editar: dados vs permissões */
  const [equipeModalTab, setEquipeModalTab] = useState<'dados' | 'permissoes'>('dados');
  const infoRef = useRef<HTMLDivElement>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const [fotoUploading, setFotoUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pendingFotoBase64, setPendingFotoBase64] = useState<string | null>(null);
  const [gerando, setGerando] = useState<string | null>(null);

  useEffect(() => {
    if (modalOpen) { setEquipeModalTab('dados'); setFieldErrors({}); }
  }, [modalOpen]);

  useEffect(() => {
    if (loadError) {
      const err = loadError as { response?: { data?: { code?: string; message?: string } } };
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.message;
      if (code === 'SECURITY_CHECK_REQUIRED') {
        toast.error('Dispositivo não autorizado. Conclua a verificação de segurança ou peça acesso direto ao administrador.');
      } else {
        toast.error(msg || 'Não foi possível carregar a equipe. Tente atualizar a página.');
      }
    }
  }, [loadError]);

  /** Atualiza campo do form e limpa erro inline */
  const setField = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) setFieldErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };
  const equipeFiltrada = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return equipe;
    return equipe.filter((u: any) =>
      [u.nome, u.email, u.cargo, u.perfil, (u as any).statusConta].join(' ').toLowerCase().includes(q)
    );
  }, [equipe, searchQuery]);


  const avatarInitial = useMemo(
    () => (form.nome.trim().charAt(0) || '?').toUpperCase(),
    [form.nome],
  );


  // Fechar popover ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setShowInfoPopover(false);
      }
    };
    if (showInfoPopover) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showInfoPopover]);

  // Auto-sync: Perfil -> Cargo
  const handlePerfilChange = (perfil: string) => {
    const cargo = PERFIL_TO_CARGO[perfil] || '';
    setForm((f) => ({
      ...f,
      perfil,
      cargo,
      permissoes:
        f.permissaoPreset === 'PADRAO'
          ? getPermissionsByRole('ESCOLA', perfil)
          : f.permissoes,
    }));
  };

  // Auto-sync: Cargo -> Perfil
  const handleCargoChange = (cargo: string) => {
    const perfil = CARGO_TO_PERFIL[cargo];
    if (perfil) {
      setForm((f) => ({
        ...f,
        cargo,
        perfil,
        permissoes:
          f.permissaoPreset === 'PADRAO'
            ? getPermissionsByRole('ESCOLA', perfil)
            : f.permissoes,
      }));
    } else {
      setForm((f) => ({ ...f, cargo }));
    }
  };

  // â•â•â• Resultado do convite (link de ativacao) â•â•â•
  const [conviteResult, setConviteResult] = useState<{
    email: string;
    activationLink: string;
    nome: string;
    cargo: string;
    perfil?: string;
    emailEnviado?: boolean;
    emailError?: string;
    enviosEmail?: number;
    statusConta?: string;
    membroId?: string;
  } | null>(null);

  // â•â•â• Detectar quais cargos faltam â•â•â•
  const { temCoordenador, temSecretaria, faltam } = useMemo(() => {
    // Qualquer membro retornado pela API com o cargo certo conta como "preenchido"
    // (inclui PENDENTE, ATIVA, etc — só excluir se statusConta for removido/bloqueado)
    const temCoord = equipe.some((u: any) => resolveRole(u) === 'COORDENACAO');
    const temSec = equipe.some((u: any) => resolveRole(u) === 'SECRETARIA');
    const missing: string[] = [];
    if (!temCoord) missing.push('Coordenador(a)');
    if (!temSec) missing.push('Secretário(a)');
    return { temCoordenador: temCoord, temSecretaria: temSec, faltam: missing };
  }, [equipe]);

  const openNew = () => {
    setEditing(null);
    const temCoord = equipe.some((u: any) => resolveRole(u) === 'COORDENACAO');
    const temSec = equipe.some((u: any) => resolveRole(u) === 'SECRETARIA');
    const defaultPerfil = temSec && !temCoord ? 'COORDENACAO' : 'SECRETARIA';
    const defaultCargo = defaultPerfil === 'COORDENACAO' ? 'Coordenador(a)' : 'Secretário(a)';

    setForm({
      nome: '',
      email: '',
      perfil: defaultPerfil,
      cargo: defaultCargo,
      telefone: '',
      permissaoPreset: 'PADRAO',
      permissoes: getPermissionsByRole(defaultPerfil, defaultCargo),
      fotoUrl: null
    });
    setConviteResult(null);
    setFieldErrors({});
    setPendingFotoBase64(null);
    setModalOpen(true);
  };
  
  const openNewCoord = () => { setEditing(null); setForm({ nome: '', email: '', perfil: 'COORDENACAO', cargo: 'Coordenador(a)', telefone: '', permissaoPreset: 'PADRAO', permissoes: getPermissionsByRole('COORDENACAO', 'Coordenador(a)'), fotoUrl: null }); setConviteResult(null); setFieldErrors({}); setPendingFotoBase64(null); setModalOpen(true); };
  const openNewSec = () => { setEditing(null); setForm({ nome: '', email: '', perfil: 'SECRETARIA', cargo: 'Secretário(a)', telefone: '', permissaoPreset: 'PADRAO', permissoes: getPermissionsByRole('SECRETARIA', 'Secretário(a)'), fotoUrl: null }); setConviteResult(null); setFieldErrors({}); setPendingFotoBase64(null); setModalOpen(true); };

  /** Abrir modal de convite ao vir do SetupWizard ("Adicionar Equipe"). */
  useEffect(() => {
    const st = location.state as { openEquipeModal?: string } | null;
    if (!st?.openEquipeModal || isLoading) return;
    if (st.openEquipeModal === 'coord') openNewCoord();
    else if (st.openEquipeModal === 'sec') openNewSec();
    else openNew();
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, isLoading, navigate, location.pathname]);
  const openEdit = (u: Usuario) => {
    const role = resolveRole(u);
    const cargo = formatCargo(u.cargo, role);
    setEditing(u);
    const permissaoPreset = ((u as any).preferencias?.permissaoPreset || 'PADRAO') as 'PADRAO' | 'PERSONALIZADO';
    setForm({
      nome: u.nome,
      email: u.email,
      perfil: role,
      cargo,
      telefone: (u as any).telefone || '',
      permissaoPreset,
      permissoes: buildPermissionMap(role, cargo, u),
      fotoUrl: (u as any).fotoUrl || null,
    });
    setConviteResult(null);
    setFieldErrors({});
    setPendingFotoBase64(null);
    setModalOpen(true);
  };

  const onAvatarFilePicked: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx. 5 MB).');
      return;
    }
    setAvatarCropFile(f);
  };

  const onAvatarCropped = async (fotoBase64: string) => {
    setAvatarCropFile(null);
    if (!editing?.id) {
      // Novo membro — armazena preview e fará upload após criação
      setPendingFotoBase64(fotoBase64);
      setForm(prev => ({ ...prev, fotoUrl: fotoBase64 }));
      return;
    }
    setFotoUploading(true);
    try {
      const { data } = await api.post<{ success: boolean; url: string }>(UPLOAD.fotoUsuario(editing.id), { fotoBase64 });
      setForm((prev) => ({ ...prev, fotoUrl: data.url }));
      setEditing((prev) => (prev ? ({ ...prev, fotoUrl: data.url } as Usuario) : null));
      const authUser = useAuthStore.getState().user;
      if (authUser && authUser.id === editing.id) {
        useAuthStore.getState().updateUserAvatar(data.url);
      }
      void refetch();
      toast.success('Foto de perfil atualizada.');
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { message?: string } } };
      toast.error(ex?.response?.data?.message || 'Não foi possível enviar a foto.');
    } finally {
      setFotoUploading(false);
    }
  };

  // â•â•â• Salvar — usa /convidar para novo, update para edicao â•â•â•
  const handleSave = async () => {
    const errors: Record<string, string> = {};
    if (!form.nome.trim()) errors.nome = 'Nome é obrigatório';
    else if (form.nome.trim().length < 3) errors.nome = 'Nome deve ter ao menos 3 caracteres';

    if (!form.email.trim()) errors.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'E-mail inválido';
    else {
      const emailDup = equipe.find((u: any) => u.email?.toLowerCase() === form.email.trim().toLowerCase() && u.id !== editing?.id);
      if (emailDup) errors.email = `E-mail já cadastrado (${emailDup.nome})`;
    }

    const phoneDigits = form.telefone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) errors.telefone = 'WhatsApp / Telefone deve ter ao menos 10 dígitos';
    else {
      const telDuplicado = equipe.find((u: any) =>
        u.id !== editing?.id && (u.telefone || '').replace(/\D/g, '') === phoneDigits && phoneDigits.length >= 10
      );
      if (telDuplicado) errors.telefone = `Telefone já cadastrado para "${telDuplicado.nome}"`;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (equipeModalTab !== 'dados') setEquipeModalTab('dados');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      if (editing) {
        const { data: result } = await api.patch(ROUTES.equipe.findOne(escolaId!, editing.id), {
          nome: form.nome,
          email: form.email,
          perfil: 'ESCOLA',
          cargo: form.cargo || form.perfil,
          telefone: form.telefone,
          permissaoPreset: form.permissaoPreset,
          permissoes: form.permissoes,
        });
        refetch();
        // Se email mudou, abrir detalhe na tab convite
        if (result.emailChanged && result.emailResult) {
          toast.success('Email atualizado e convite reenviado!');
          setModalOpen(false);
          setAvatarCropFile(null);
          setTimeout(() => { setFichaId(editing.id); setFichaTab('convite'); }, 200);
        } else {
          toast.success('Membro atualizado!');
          setModalOpen(false);
          setAvatarCropFile(null);
        }
      } else {
        // Usar endpoint /convidar para gerar link de ativacao
        if (!escolaId) { toast.error('Escola nao identificada'); setSaving(false); return; }
        const { data: result } = await api.post(ROUTES.equipe.convidar(escolaId), {
          nome: form.nome,
          email: form.email,
          cargo: form.cargo || form.perfil,
          telefone: form.telefone || undefined,
          acessoDireto: true,
          permissaoPreset: form.permissaoPreset,
          permissoes: form.permissoes,
        });
        refetch();
        void queryClient.invalidateQueries({ queryKey: ['equipe'] });
        setModalOpen(false);
        setAvatarCropFile(null);
        // Upload foto pendente (novo membro)
        const newId = result.usuario?.id;
        if (newId && pendingFotoBase64) {
          try {
            await api.post(ROUTES.upload.fotoUsuario(newId), { fotoBase64: pendingFotoBase64 });
            void refetch();
            void queryClient.invalidateQueries({ queryKey: ['equipe'] });
          } catch { /* foto será enviada depois */ }
          setPendingFotoBase64(null);
        }
        // Salvar resultado do convite para exibir o InviteResultDialog
        if (newId) {
          setConviteResult({
            email: form.email,
            activationLink: result.activationLink || '',
            nome: form.nome,
            cargo: form.cargo || form.perfil,
            perfil: form.perfil,
            emailEnviado: result.emailResult?.emailEnviado ?? result.emailEnviado,
            emailError: result.emailResult?.emailError ?? result.emailError,
            membroId: newId,
          });
        }
      }
    } catch (e: any) {
      const errorObj = e?.response?.data?.error || e?.response?.data;
      const msg = errorObj?.message || e?.message || 'Erro ao salvar';
      const code = errorObj?.code;
      const msgLower = (msg || '').toLowerCase();
      
      const isEmailConflict = msgLower.includes('email') || (code === 'CONFLICT' && msgLower.includes('email'));
      const isPhoneConflict = msgLower.includes('telefone') || msgLower.includes('whatsapp') || (code === 'CONFLICT' && msgLower.includes('telefone'));

      if (isEmailConflict) {
        setFieldErrors(prev => ({ ...prev, email: msg }));
        if (equipeModalTab !== 'dados') setEquipeModalTab('dados');
      } else if (isPhoneConflict) {
        setFieldErrors(prev => ({ ...prev, telefone: msg }));
        if (equipeModalTab !== 'dados') setEquipeModalTab('dados');
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ═══ Gerar novo link de ativação (edit modal inline) ═══
  const gerarNovoLink = async (membro: Usuario) => {
    if (!escolaId) return;
    setGerando(membro.id);
    try {
      const { data } = await api.post(ROUTES.equipe.convidar(escolaId), {
        nome: membro.nome, email: membro.email, cargo: resolveRole(membro),
        telefone: (membro as any).telefone || undefined, acessoDireto: true,
      });
      const rawLink = data.activationLink || '';
      if (rawLink) {
        const linkNormalizado = normalizeActivationLink(rawLink);
        await navigator.clipboard.writeText(linkNormalizado).catch(() => {});
        toast.success('Link copiado!');
      }
      refetch();
      void queryClient.invalidateQueries({ queryKey: ['equipe'] });
    } catch { toast.error('Erro ao gerar link'); }
    setGerando(null);
  };

  // ═══ Reenviar email de convite (max 3x) ═══
  const handleReenviar = async () => {
    const membroId = conviteResult?.membroId;
    if (!membroId || !escolaId) return;
    setReenviando(true);
    try {
      const { data } = await api.post(ROUTES.equipe.reenviar(escolaId, membroId));
      setConviteResult(prev => prev ? {
        ...prev,
        emailEnviado: data.emailEnviado,
        emailError: data.emailError || undefined,
        enviosEmail: data.enviosEmail,
        statusConta: data.statusConta,
        activationLink: data.activationLink || prev.activationLink,
      } : null);
      refetch();
      if (data.emailEnviado) {
        toast.success(`Email reenviado! (${data.enviosEmail}/3)`);
      } else {
        toast.error(`Falha no envio: ${data.emailError || 'erro desconhecido'}`);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Erro ao reenviar';
      toast.error(msg);
    } finally {
      setReenviando(false);
    }
  };

  // ═══ Salvar email editado inline no resultado ═══
  const handleSaveEmail = async () => {
    if (!editEmailValue.trim() || !conviteResult?.membroId || !escolaId) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmailValue)) { toast.error('Email invalido'); return; }
    setSavingEmail(true);
    try {
      const { data: result } = await api.patch(ROUTES.equipe.findOne(escolaId, conviteResult.membroId), {
        email: editEmailValue,
      });
      refetch();
      if (result.emailResult) {
        setConviteResult(prev => prev ? {
          ...prev,
          email: editEmailValue,
          emailEnviado: result.emailResult.emailEnviado,
          emailError: result.emailResult.emailError || undefined,
          enviosEmail: result.emailResult.enviosEmail || 1,
          statusConta: result.emailResult.statusConta || 'PENDENTE',
          activationLink: result.emailResult.activationLink || prev.activationLink,
        } : null);
        toast.success('Email atualizado e convite reenviado!');
      }
      setEditingEmail(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao atualizar email');
    } finally {
      setSavingEmail(false);
    }
  };

  const equipeStatusLabels: Record<string, string> = { SUSPENSA: 'suspender', BLOQUEADA: 'bloquear', ATIVA: 'reativar' };
  const requestDeleteMembro = (membro: Usuario) => setDeleteConfirm(membro);
  const confirmDeleteMembro = async () => {
    if (!deleteConfirm) return;
    const membro = deleteConfirm;
    const isPendente = (membro as any).statusConta === 'PENDENTE' || (membro as any).statusConta === 'ATIVANDO';
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(membro.id);
      toast.success(isPendente ? 'Convite removido' : 'Membro desativado');
      refetch();
      void queryClient.invalidateQueries({ queryKey: ['equipe'] });
    } catch { toast.error('Erro ao remover'); }
  };

  const requestChangeStatus = (membro: Usuario, novoStatus: string) => setStatusConfirm({ membro, novoStatus });
  const confirmChangeStatusEquipe = async () => {
    if (!statusConfirm || !escolaId) return;
    const { membro, novoStatus } = statusConfirm;
    setStatusConfirm(null);
    try {
      await api.patch(ROUTES.equipe.status(escolaId, membro.id), { statusConta: novoStatus });
      toast.success(`Membro ${equipeStatusLabels[novoStatus] || 'atualizado'}!`);
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Erro ao alterar status';
      toast.error(msg);
    }
  };


  // â•â•â• Status badge helper (padrão Professores.tsx) â•â•â•
  // Prioridade: ativo=false â†’ Inativo | statusConta especial | ativo=true â†’ Ativo
  const statusBadge = (u: Usuario) => {
    const status = (u as any).statusConta;
    // 1. Se desativado (soft-delete), sempre mostrar Inativo
    if (!u.ativo) return <span className="badge badge-inactive">Inativo</span>;
    // 2. StatusConta especiais
    if (status === 'SUSPENSA') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
        <Ban size={12} /> Suspenso
      </span>
    );
    if (status === 'BLOQUEADA') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>
        <ShieldOff size={12} /> Bloqueado
      </span>
    );
    if (status === 'FALHOU') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>
        <XCircle size={12} /> Falhou
      </span>
    );
    if (status === 'PENDENTE' || status === 'ATIVANDO') return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
        <Clock size={12} /> Pendente
      </span>
    );
    // 3. Ativo normal
    return <span className="badge badge-active">Ativo</span>;
  };

  const handleReactivateMembro = async (u: Usuario) => {
    try {
      await api.patch(ROUTES.equipe.findOne(escolaId!, u.id), { ativo: true });
      toast.success(`"${u.nome}" ativado com sucesso!`);
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao ativar membro.');
    }
  };

  const handleDesativarMembro = async (u: Usuario) => {
    try {
      await api.patch(ROUTES.equipe.findOne(escolaId!, u.id), { ativo: false });
      toast.success(`"${u.nome}" desativado.`);
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao desativar membro.');
    }
  };

  const getRowActionItems = (u: Usuario): RowActionItem[] => {
    const status = (u as any).statusConta;
    const isPendente = status === 'PENDENTE' || status === 'ATIVANDO' || status === 'FALHOU';
    const isDiretor = userCargo === 'DIRETOR' || userPerfil === 'ADMIN';

    const items: (RowActionItem | null)[] = [
      {
        label: isPendente ? 'Ver Convite' : 'Detalhes do Membro',
        icon: <Eye size={15} />,
        onClick: () => {
          setFichaId(u.id);
          setFichaTab(isPendente ? 'convite' : 'dados');
        },
      },
      {
        label: 'Editar Dados',
        icon: <Pencil size={15} />,
        onClick: () => handlePermissionClick(canManage, () => openEdit(u), 'Você não tem permissão para editar membros da equipe.'),
        variant: !canManage ? 'disabled' : undefined,
      },
      devSettings.allowDeleteEquipe && isPendente
        ? {
            label: 'Remover Convite',
            icon: <Trash2 size={15} />,
            onClick: () => handlePermissionClick(isDiretor, () => requestDeleteMembro(u), 'Apenas Diretor ou Administrador pode remover convites.'),
            variant: isDiretor ? 'danger' : 'disabled',
          }
        : null,
      devSettings.allowDeleteEquipe && !isPendente && u.ativo
        ? {
            label: 'Desativar Acesso',
            icon: <PowerOff size={15} />,
            onClick: () => handlePermissionClick(canManage, () => handleDesativarMembro(u), 'Você não tem permissão para desativar membros da equipe.'),
            variant: canManage ? 'danger' : 'disabled',
          }
        : null,
      devSettings.allowDeleteEquipe && !isPendente && !u.ativo
        ? {
            label: 'Reativar Acesso',
            icon: <RotateCcw size={15} />,
            onClick: () => handlePermissionClick(canManage, () => handleReactivateMembro(u), 'Você não tem permissão para reativar membros da equipe.'),
            variant: canManage ? 'success' : 'disabled',
          }
        : null,
    ];

    return items.filter(Boolean) as RowActionItem[];
  };

  const renderRowActions = (u: Usuario) => {
    const status = (u as any).statusConta;
    const isPendente = status === 'PENDENTE' || status === 'ATIVANDO' || status === 'FALHOU';
    const isDiretor = userCargo === 'DIRETOR' || userPerfil === 'ADMIN';
    const actionItems = getRowActionItems(u);

    return (
      <div className="flex items-center">
        {/* Mobile: 3 pontinhos verticais com menu de contexto rotulado */}
        <div className="flex md:hidden">
          <RowActionMenu items={actionItems} />
        </div>

        {/* Desktop: Ações diretas horizontais */}
        <div className="hidden md:flex items-center gap-1">
          <button className="action-btn action-btn-view" title="Detalhes" onClick={() => { setFichaId(u.id); setFichaTab(isPendente ? 'convite' : 'dados'); }}><Eye size={15} /></button>
          <button
            className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
            title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
            onClick={() => handlePermissionClick(canManage, () => openEdit(u), 'Você não tem permissão para editar membros da equipe.')}
          >
            <Pencil size={15} />
          </button>
          {devSettings.allowDeleteEquipe && (
            isPendente ? (
              <button
                className={`action-btn action-btn-danger ${!isDiretor ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={!isDiretor ? 'Apenas Diretores podem remover convites' : 'Remover'}
                onClick={() => handlePermissionClick(isDiretor, () => requestDeleteMembro(u), 'Apenas Diretor ou Administrador pode remover convites.')}
              >
                <Trash2 size={15} />
              </button>
            ) : u.ativo ? (
              <button
                className={`action-btn action-btn-danger ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={!canManage ? 'Permissão restrita para desativação' : 'Desativar'}
                onClick={() => handlePermissionClick(canManage, () => handleDesativarMembro(u), 'Você não tem permissão para desativar membros da equipe.')}
              >
                <PowerOff size={15} />
              </button>
            ) : (
              <button
                className={`action-btn action-btn-success ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={!canManage ? 'Permissão restrita para reativação' : 'Ativar'}
                onClick={() => handlePermissionClick(canManage, () => handleReactivateMembro(u), 'Você não tem permissão para reativar membros da equipe.')}
              >
                <RotateCcw size={15} />
              </button>
            )
          )}
        </div>
      </div>
    );
  };

  const activePermCount = useMemo(
    () => Object.values(form.permissoes).filter(Boolean).length,
    [form.permissoes],
  );

  const membroFicha = fichaId ? (equipe.find((u) => u.id === fichaId) as Usuario | undefined) : undefined;

  const columns: ColumnDef<Usuario>[] = [
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, opacity: u.ativo ? 1 : 0.45 }}>
            <ListAvatar fotoUrl={(u as any).fotoUrl} nome={u.nome || 'Membro'} role={u.cargo || resolveRole(u)} size={40} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.nome}
              </div>
              <div style={{ fontSize: '.76rem', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <a
                  href={`mailto:${u.email}`}
                  style={{ color: '#2563eb', textDecoration: 'underline', textUnderlineOffset: 2 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {u.email}
                </a>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'cargo', header: 'Cargo',
      cell: ({ row }) => {
        const u = row.original;
        const role = resolveRole(u);
        return <span style={{ opacity: u.ativo ? 1 : 0.45 }}>{formatCargo(u.cargo, role)}</span>;
      },
    },
    {
      accessorKey: 'perfil', header: 'Perfil',
      cell: ({ row }) => {
        const u = row.original;
        const role = resolveRole(u);
        const colors: Record<string, string> = {
          DIRETOR: '#6366f1', VICE_DIRETOR: '#059669', COORDENACAO: '#059669', SECRETARIA: '#2563eb',
        };
        const c = colors[role] || '#6b7280';
        return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, background: `${c}15`, color: c, opacity: u.ativo ? 1 : 0.45 }}>{role === 'VICE_DIRETOR' ? 'Vice Diretor' : role}</span>;
      },
    },
    {
      accessorKey: 'ativo', header: 'Status',
      cell: ({ row }) => statusBadge(row.original),
    },
    {
      id: 'registro',
      header: 'Registro',
      cell: ({ row }) => {
        const u = row.original as Usuario & { criadoEm?: string; atualizadoEm?: string };
        if (!u.criadoEm) return <span className="ac-resp-registro ac-resp-registro--empty">—</span>;
        return (
          <RegistroMeta
            criadoEm={u.criadoEm}
            atualizadoEm={u.atualizadoEm}
          />
        );
      },
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => renderRowActions(row.original),
    },
  ];

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Hero Guia — orientar cadastro e gestão da equipe */}
      {!isLoading && (
        <SetupPageHero
          icon={UserCog}
          etapa={1}
          titulo="Equipe Administrativa"
          descricao={
            faltam.length === 2
              ? 'Sua escola precisa de Coordenador(a) e Secretário(a). Cadastre-os para que possam ajudar na configuração.'
              : faltam.length > 0
                ? `Falta cadastrar: ${faltam.join(', ')}. ${temCoordenador ? '✓ Coordenador(a) já cadastrado.' : '✓ Secretário(a) já cadastrado.'}`
                : 'Gerencie a equipe administrativa da escola. Cadastre e atualize acessos de coordenadores, diretores e secretários.'
          }
          dica={
            faltam.length > 0
              ? [
                  temCoordenador ? '✓ Coordenador(a) — cadastrado' : '○ Coordenador(a) — configura Turnos, Matérias, Turmas e Grade de Horários',
                  temSecretaria ? '✓ Secretário(a) — cadastrado' : '○ Secretário(a) — matricula alunos, gerencia responsáveis e documentos',
                ].join(' | ')
              : undefined
          }
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}


      <div
        className="ac-admin-data-card"
        style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar membro..."
          actions={
            <button
              type="button"
              className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
              onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para cadastrar membros na equipe.')}
              title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Adicionar novo membro à equipe'}
              style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Plus size={15} /> Adicionar Membro
            </button>
          }
          totalRecords={equipeFiltrada.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[6, 12, 24, 48]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable columns={columns} data={equipeFiltrada} isLoading={isLoading} searchPlaceholder="Buscar membro..." showToolbar={false} pageSize={pageSize} />
          ) : (
            <EquipeCardView
              equipeFiltrada={equipeFiltrada}
              statusBadge={statusBadge}
              resolveRole={resolveRole}
              renderRowActions={renderRowActions}
            />
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={equipeFiltrada.length}
              pageSize={pageSize}
            />
          )}
        </div>
      </div>

      <InviteMemberModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setAvatarCropFile(null); }}
        editing={editing}
        saving={saving}
        onSave={handleSave}
        gerarNovoLink={gerarNovoLink}
        gerando={gerando}
        userId={userId}
        canManagePermissions={canManagePermissions}
        form={form}
        setForm={setForm}
        fieldErrors={fieldErrors}
        setFieldErrors={setFieldErrors}
        avatarInitial={avatarInitial}
        avatarFileInputRef={avatarFileInputRef}
        onAvatarFilePicked={onAvatarFilePicked}
        fotoUploading={fotoUploading}
        formatPhone={formatPhone}
        equipe={equipe}
        equipeModalTab={equipeModalTab}
        setEquipeModalTab={setEquipeModalTab}
      />

      <Modal
        title=""
        open={!!fichaId && !!membroFicha}
        onClose={() => setFichaId(null)}
        size="wide"
        contentClassName="ac-ficha-modal ac-equipe-ficha-modal"
        hideCloseButton
        footer={
          membroFicha ? (
            <EquipeFichaFooter
              membro={membroFicha as Usuario & { criadoEm?: string }}
              canManage={canManage}
              onEdit={() => {
                setFichaId(null);
                openEdit(membroFicha);
              }}
              onDeactivate={() => {
                setFichaId(null);
                handleDesativarMembro(membroFicha);
              }}
              onReactivate={() => {
                setFichaId(null);
                handleReactivateMembro(membroFicha);
              }}
            />
          ) : null
        }
      >
        {membroFicha && escolaId && (
          <EquipeFichaDesktop
            key={fichaId!}
            membro={membroFicha as unknown as Record<string, unknown>}
            escolaId={escolaId}
            canManage={canManage}
            initialTab={fichaTab}
            onClose={() => setFichaId(null)}
            onEdit={() => {
              setFichaId(null);
              openEdit(membroFicha);
            }}
            onDeactivate={() => {
              setFichaId(null);
              handleDesativarMembro(membroFicha);
            }}
            onReactivate={() => {
              setFichaId(null);
              handleReactivateMembro(membroFicha);
            }}
            onRefetch={() => refetch()}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm}
        title={
          deleteConfirm && ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
            ? 'Remover convite'
            : 'Desativar membro'
        }
        message={
          deleteConfirm
            ? ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
              ? `Remover o convite de "${deleteConfirm.nome}"? O registro será excluído permanentemente.`
              : `Desativar "${deleteConfirm.nome}"? O membro perderá acesso mas os dados serão preservados.`
            : ''
        }
        confirmLabel={
          deleteConfirm && ((deleteConfirm as any).statusConta === 'PENDENTE' || (deleteConfirm as any).statusConta === 'ATIVANDO')
            ? 'Remover'
            : 'Desativar'
        }
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteMembro()}
      />

      <ConfirmModal
        open={!!statusConfirm}
        title="Alterar status"
        message={
          statusConfirm
            ? `Deseja ${equipeStatusLabels[statusConfirm.novoStatus] || 'alterar'} "${statusConfirm.membro.nome}"?`
            : ''
        }
        confirmLabel="Confirmar"
        onCancel={() => setStatusConfirm(null)}
        onConfirm={() => void confirmChangeStatusEquipe()}
      />

      {conviteResult && (
        <InviteResultDialog
          open={true}
          nome={conviteResult.nome}
          email={conviteResult.email}
          cargo={conviteResult.cargo}
          perfil={conviteResult.perfil || form.perfil}
          telefone={form.telefone || undefined}
          activationLink={conviteResult.activationLink}
          emailSent={conviteResult.emailEnviado}
          onClose={() => setConviteResult(null)}
        />
      )}

      {avatarCropFile && (
        <AvatarCropper
          file={avatarCropFile}
          onCropped={onAvatarCropped}
          onCancel={() => setAvatarCropFile(null)}
        />
      )}
    </div>
  );
}
